import type { BoxProps } from '@chakra-ui/react';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  Box,
  Flex,
  Text,
  useToken,
} from '@chakra-ui/react';
import { MotionAccordionPanel } from '@time-line/components/common';
import { Minus, Plus } from '@time-line/components/icons';
import { useHeader } from 'hooks/useHeader';
import { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { getElementHeight } from 'utils/getElementHeight';

import type { ContentItem } from './ContentsContent';
import { ContentsContent } from './ContentsContent';

interface MobileStickyContentsProps extends BoxProps {
  contents: ContentItem[];
  hasDisclaimer?: boolean;
  hasReferences?: boolean;
  hasTranscript?: boolean;
  hasKeyTakeaways?: boolean;
  hasContributors?: boolean;
}

export function MobileStickyContents({
  contents,
  hasDisclaimer,
  hasReferences,
  hasTranscript,
  hasKeyTakeaways,
  hasContributors,
  ...props
}: MobileStickyContentsProps) {
  const { isFixed: isHeaderFixed } = useHeader();
  const headerHeight = useToken('space', '2xl');
  const [leftPos, setLeftPos] = useState(0);
  const [tocWidth, setTocWidth] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const [accordionIndex, setAccordionIndex] = useState(-1);

  useEffect(() => {
    // If accordion is open
    if (accordionIndex === 0) {
      const parentEl = parentRef.current?.getBoundingClientRect();
      const headerComputedHeight = getElementHeight('header');

      // If table of contents is equal to or less than header height from top, we presume it's pinned to top
      if (parentEl && parentEl.top <= headerComputedHeight) {
        // We show the overlay
        setShowOverlay(true);
        // And prevent background scrolling
        document.body.style.overflow = 'hidden';
        return;
      }
    }
    // If the accordon is closed we can resume background scrolling
    document.body.style.removeProperty('overflow');
    if (showOverlay) {
      // And hide the overlay if it's showing
      setShowOverlay(false);
    }
  }, [showOverlay, accordionIndex]);

  useEffect(() => {
    if (parentRef.current) {
      const leftPosition = parentRef.current.getBoundingClientRect().left;
      const w = document.body.clientWidth;
      setLeftPos(leftPosition);
      setTocWidth(w);
    }
  }, [width, showOverlay]);

  return (
    <Box
      ref={parentRef}
      id="blog-contents"
      mt="m"
      mb="lg"
      display={{ lg: 'none' }}
      position="sticky"
      top={isHeaderFixed ? headerHeight : 0}
      zIndex={3}
      transition="all 0.2s ease-in-out 0s"
      {...props}
    >
      <Box
        opacity={showOverlay ? 1 : 0}
        pointerEvents={showOverlay ? 'all' : 'none'}
        position="fixed"
        transition="opacity 0.2s ease-in-out 0s"
        top={0}
        right={0}
        left={0}
        bottom={0}
        bg="blackAlpha.400"
        zIndex={-1}
        onClick={() => setAccordionIndex(-1)}
      />
      <Box width="100%" zIndex={4}>
        <Accordion allowMultiple index={[accordionIndex]}>
          <AccordionItem
            border={0}
            sx={{
              '&:before, &:after': {
                content: "''",
                position: 'absolute',
                left: `-${leftPos}px`,
                width: tocWidth,
              },
              '&:before': {
                height: '100%',
                backgroundColor: 'gray.50',
                zIndex: -1,
              },
              '&:after': {
                height: '1px',
                backgroundColor: 'blackAlpha.250',
              },
            }}
          >
            {({ isExpanded }) => (
              <>
                <AccordionButton
                  px={0}
                  py={0}
                  sx={{
                    '&:hover': { bgColor: 'initial' },
                  }}
                  onClick={() =>
                    setAccordionIndex(accordionIndex === 0 ? -1 : 0)
                  }
                >
                  <Flex
                    width="100%"
                    py="2xs"
                    justifyContent="space-between"
                    alignItems="center"
                    gridRowGap="xs"
                  >
                    <Text as="h3" size="title2" textAlign="left">
                      Table of contents
                    </Text>
                    {isExpanded ? (
                      <Minus boxSize="sm" />
                    ) : (
                      <Plus
                        boxSize="sm"
                        _groupHover={{
                          transform: !isExpanded && 'rotate(90deg)',
                        }}
                        transitionProperty="common"
                        transitionDuration="normal"
                      />
                    )}
                  </Flex>
                </AccordionButton>
                <MotionAccordionPanel px={0} py="xs">
                  <ContentsContent
                    contents={contents}
                    hasDisclaimer={hasDisclaimer}
                    hasReferences={hasReferences}
                    hasTranscript={hasTranscript}
                    hasKeyTakeaways={hasKeyTakeaways}
                    hasContributors={hasContributors}
                    onItemClick={() => setAccordionIndex(-1)}
                  />
                </MotionAccordionPanel>
              </>
            )}
          </AccordionItem>
        </Accordion>
      </Box>
    </Box>
  );
}
